import { graphTypeEnum } from '@/features/analysis/_models/project';
import { useCurrentFiscalYear } from '@/features/search/_hooks/get-current-fiscal-year';
import { SEARCH_DEBOUNCE_MS_TIME } from '@/features/search/_utils/constants';
import { useDebouncedQueryStates } from '@/lib/nuqs';
import { ksAggregationTargetTypeEnum, ksAnalysisAxisEnum } from '@/utils/enum';
import {
  parseAsArrayOf,
  parseAsBoolean,
  parseAsInteger,
  parseAsString,
  parseAsStringLiteral,
} from 'nuqs';
import { initialValueOfKsAnalysisSearchForm } from '../_models/ks-analysis-search';

/**
 * 基金シート分析画面 (`/analysis/fund-sheet`) の URL クエリ ↔ state 同期。
 * RS 用 `useAnalysisPageSearchQueryStates` とは独立。
 */
export const useFundSheetAnalysisPageSearchQueryStates = ({
  useDebounceControl = false,
}: {
  useDebounceControl?: boolean;
} = {}) => {
  const currentFiscalYear = useCurrentFiscalYear();
  const year = currentFiscalYear.data ?? currentFiscalYear.basicFiscalYear;
  const initialValue = initialValueOfKsAnalysisSearchForm(year);
  const queryStates = useDebouncedQueryStates({
    keyMap: {
      page: parseAsInteger.withDefault(1),
      pageSize: parseAsInteger.withDefault(30),
      fiscalYear: parseAsInteger.withDefault(initialValue.fiscalYear),
      q: parseAsString,
      isSearchTargetProjectName: parseAsBoolean.withDefault(false),
      cascader: parseAsString,
      corporateNumber: parseAsString,
      projectNumbers: parseAsString,
      // PS 用 (ProjectsSearchAccordionInitializeStore が参照)
      organizationIds: parseAsArrayOf(parseAsString),
      // KS では使わないが、`ProjectsSearchAccordionInitializeStore` がユニオン型の
      // 全プロパティを参照するため、null デフォルトで keyMap に含めておく。
      otherResponsibleOrganizationIds: parseAsArrayOf(parseAsString),
      policyIds: parseAsArrayOf(parseAsString),
      expenditureBudgetPurposeIds: parseAsArrayOf(parseAsString),
      aggregationTargetType: parseAsStringLiteral(
        ksAggregationTargetTypeEnum.options,
      ).withDefault(initialValue.aggregationTargetType),
      // analysisAxis は URL を単一の真実とし、`withDefault` で初期表示を organization にする。
      // 「合計のみ（軸なし）」は URL から消すのではなく `total` センチネルで明示的に表現する
      // （フォームでは null、API では除外）。検索＝URL 変更でアコーディオンが再マウントされても、
      // 「初回表示（=organization）」と「軸クリア（=total）」を URL だけで区別でき、
      // 見た目と検索結果が一致する（RS-4424）。
      analysisAxis: parseAsStringLiteral(
        ksAnalysisAxisEnum.options,
      ).withDefault(initialValue.analysisAxis),
      // 形式: `{sortOrder}_{columnKey}` (例: "ascend_2023")
      order: parseAsString,
      // 円グラフ・ツリーマップの年度タブで選択中の表示年度。`order`（ソート状態）とは独立させ、
      // ソート解除（order=null）で表示年度が既定へ巻き戻らないようにする (RS-4491)。
      chartYear: parseAsInteger,
      graphType: parseAsStringLiteral(graphTypeEnum.options).withDefault(
        graphTypeEnum.Values.stackedVerticalBar,
      ),
    },
    debounceMs: SEARCH_DEBOUNCE_MS_TIME,
    useDebounceControl,
  });
  return queryStates;
};

export type FundSheetAnalysisPageSearchQueryStates = ReturnType<
  typeof useFundSheetAnalysisPageSearchQueryStates
>['searchQueryStates'];
